import React, { MouseEventHandler, PropsWithChildren } from 'react';
import { EditorPlugin } from 'draft-js-plugins-editor';
import linkifyIt, { LinkifyIt } from 'linkify-it';
import tlds from 'tlds';
import { ContentBlock, ContentState, DraftDecorator, EditorState } from 'draft-js';

interface EditorFnRefs {
  getEditorState?: () => EditorState;
}

const linkify: LinkifyIt = linkifyIt().tlds(tlds);

export function extractLinks(text: string): linkifyIt.Match[] | null {
  return linkify.match(text);
}

function Link({ decoratedText, children }: PropsWithChildren<{ decoratedText: string }>) {
  const link = extractLinks(decoratedText)?.[0];
  const href = link.url;
  const handleClick: MouseEventHandler = (e) => {
    e.preventDefault();
    window.open(href, '_blank');
  };

  return <a href={href} target="_blank" onClick={handleClick} children={children} />;
}

const inMiddleOfMarkdownLink = (blockText: string, matchIdx: number): boolean => {
  const precedingString = blockText.slice(0, matchIdx);
  const reversed = Array.from(precedingString).reverse().join('');
  return /^\(\][\w\-\s]+\[/.test(reversed);
};

const isLinkEntity = (
  match: linkifyIt.Match,
  contentBlock: ContentBlock,
  contentState: ContentState,
): boolean => {
  for (let charIdx = match.index; charIdx <= match.lastIndex; charIdx += 1) {
    const entityKey = contentBlock.getEntityAt(charIdx);
    if (entityKey && contentState) {
      return contentState.getEntity(entityKey)?.getType() == 'LINK';
    }
  }
  return false;
};

function linkStrategy(
  editorFns: EditorFnRefs,
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
) {
  const contentState = editorFns.getEditorState?.()?.getCurrentContent();
  const blockText = contentBlock?.getText();
  const linkTextSpans = extractLinks(blockText)?.filter((match) => {
    return (
      !inMiddleOfMarkdownLink(blockText, match.index) &&
      !isLinkEntity(match, contentBlock, contentState)
    );
  });

  if (linkTextSpans?.length > 0) {
    for (const textSpan of linkTextSpans) {
      callback(textSpan.index, textSpan.lastIndex);
    }
  }
}

const createLinkifyStrategy =
  (editorFns: EditorFnRefs): DraftDecorator['strategy'] =>
  (contentBlock, callback) =>
    linkStrategy(editorFns, contentBlock, callback);

export default function createLinkifyPlugin(): EditorPlugin {
  const editorFns: EditorFnRefs = {};

  return {
    initialize: ({ getEditorState }) => {
      editorFns.getEditorState = getEditorState;
    },

    decorators: [
      {
        strategy: createLinkifyStrategy(editorFns),
        component: Link,
      },
    ],
  };
}
